import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

interface UserProfile {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
  created_at: string;
}

function UsersList() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verifica che l'utente corrente sia admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Non autenticato');

      const { data: currentProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('user_id', currentUser.id)
        .single();

      if (!currentProfile || currentProfile.role !== 'admin') {
        throw new Error('Non autorizzato - Solo gli amministratori possono gestire gli utenti');
      }

      // Recupera tutti i profili utente
      const { data: profiles, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching profiles:', fetchError);
        throw new Error('Errore nel caricamento dei profili utente');
      }

      setUsers(profiles || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
      toast.error(error.message || 'Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Verifica che l'utente corrente sia admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Non autenticato');

      const { data: adminProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('user_id', currentUser.id)
        .single();

      if (!adminProfile || adminProfile.role !== 'admin') {
        throw new Error('Non autorizzato - Solo gli amministratori possono creare nuovi utenti');
      }

      // Verifica se l'utente esiste già
      const { data: existingUser } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('email', newUser.email)
        .single();

      if (existingUser) {
        throw new Error('Un utente con questa email esiste già');
      }

      // Crea il nuovo utente
      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email: newUser.email,
        password: newUser.password,
        options: {
          data: {
            name: newUser.name,
            role: newUser.role
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!user) throw new Error('Errore nella creazione dell\'utente');

      // Conferma l'email automaticamente
      const { error: updateError } = await supabase.auth.admin.updateUserById(
        user.id,
        { email_confirm: true }
      );

      if (updateError) throw updateError;

      // Crea il profilo utente
      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          user_id: user.id,
          name: newUser.name,
          email: newUser.email,
          role: newUser.role
        });

      if (profileError) throw profileError;

      await fetchUsers(); // Aggiorna la lista
      toast.success('Utente creato con successo');
      setShowNewUserForm(false);
      setNewUser({ email: '', password: '', name: '', role: 'user' });
    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(error.message);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo utente?')) return;
    
    setError(null);

    try {
      // Verifica che l'utente corrente sia admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Non autenticato');

      const { data: adminProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('user_id', currentUser.id)
        .single();

      if (!adminProfile || adminProfile.role !== 'admin') {
        throw new Error('Non autorizzato - Solo gli amministratori possono eliminare utenti');
      }

      // Elimina il profilo utente
      const { error: deleteProfileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', userId);

      if (deleteProfileError) throw deleteProfileError;

      // Elimina l'utente da auth
      const { error: deleteAuthError } = await supabase.auth.admin.deleteUser(userId);
      if (deleteAuthError) throw deleteAuthError;
      
      toast.success('Utente eliminato con successo');
      await fetchUsers(); // Aggiorna la lista
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.message);
      toast.error(`Errore: ${error.message}`);
    }
  };

  return (
    <AdminLayout title="Gestione Utenti">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl font-semibold">Utenti</h2>
        <button
          onClick={() => setShowNewUserForm(true)}
          className="bg-primary hover:bg-primary-light text-white px-4 py-2.5 rounded-md transition-colors flex items-center justify-center w-full sm:w-auto"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuovo Utente
        </button>
      </div>

      {showNewUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-md w-full my-auto max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">Nuovo Utente</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={e => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={8}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ruolo
                </label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value as 'user' | 'admin' }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                >
                  <option value="user">Utente</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewUserForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors"
                >
                  Crea Utente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
          <p className="mt-2 text-gray-600">Caricamento utenti...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Si è verificato un errore:</p>
          <p>{error}</p>
        </div>
      ) : (
        <>
        {/* Vista card: sotto md la tabella a 5 colonne non è leggibile */}
        <div className="md:hidden space-y-3">
          {users.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
              Nessun utente trovato
            </div>
          ) : (
            users.map((user) => (
              <div key={user.id} className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{user.name}</div>
                    <div className="text-sm text-gray-600 break-all mt-0.5">{user.email}</div>
                  </div>
                  <button
                    onClick={() => handleDeleteUser(user.user_id)}
                    className="text-red-600 hover:text-red-700 p-2 -m-1 flex-shrink-0"
                    title="Elimina"
                    aria-label={`Elimina ${user.name}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.role === 'admin'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role === 'admin' ? 'Amministratore' : 'Utente'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Nome</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Ruolo</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Data Creazione</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    Nessun utente trovato
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="py-4 px-4">{user.name}</td>
                    <td className="py-4 px-4">{user.email}</td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        user.role === 'admin' 
                          ? 'bg-primary/10 text-primary' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role === 'admin' ? 'Amministratore' : 'Utente'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      {new Date(user.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDeleteUser(user.user_id)}
                          className="text-red-600 hover:text-red-700"
                          title="Elimina"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
        </>
      )}
    </AdminLayout>
  );
}

export default UsersList;